import React, { createContext, useEffect, useReducer } from 'react';
import { MatxLoading } from '../components';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { clearAdminImpersonationSession } from '../utils/localStorage';
import { clearActiveProvider, saveActiveProvider } from '../utils/tenantApi';

const initialState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
};

const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }

  const decodedToken = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;
  return decodedToken.exp > currentTime;
};

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INIT': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
      };
    }
    case 'LOGIN': {
      const { user } = action.payload;
      return {
        ...state,
        isAuthenticated: true,
        user,
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    }
    case 'REGISTER': {
      const { user } = action.payload;
      return {
        ...state,
        isAuthenticated: true,
        user,
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialState,
  method: 'JWT',
  login: () => Promise.resolve(),
  logout: () => {},
  register: () => Promise.resolve(),
  switchCompany: () => false,
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const login = (response) => {
    const user = response?.data || response;
    const { access_token } = user;
    saveActiveProvider(user?.provider);
    setSession(access_token);
    localStorage.setItem('user', JSON.stringify(user));
    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });
  };

  const register = (response) => {
    const user = response?.data || response;
    const { access_token } = user;
    saveActiveProvider(user?.provider || window.localStorage.getItem('tenantProvider'));
    localStorage.setItem('user', JSON.stringify(user));

    setSession(access_token);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  };

  const switchCompany = (company) => {
    if (!company?.identifier || !company?.access_token || !company?.refresh_token || !company?.user_id || !company?.role) {
      return false;
    }

    const companies = Array.isArray(state.user?.companies) ? state.user.companies : [];
    const user = {
      ...state.user,
      provider: company.identifier,
      id: company.user_id,
      user_id: company.user_id,
      role: company.role,
      access_token: company.access_token,
      refresh_token: company.refresh_token,
      company,
      companies,
    };

    saveActiveProvider(company.identifier);
    setSession(company.access_token);
    window.localStorage.setItem('user', JSON.stringify(user));
    window.localStorage.removeItem('allBookingdata');
    dispatch({ type: 'LOGIN', payload: { user } });
    return true;
  };

  const logout = () => {
    setSession(null);
    clearAdminImpersonationSession();
    window.localStorage.removeItem('user');
    window.localStorage.removeItem('allBookingdata');
    clearActiveProvider();
    dispatch({ type: 'LOGOUT' });
  };

  useEffect(() => {
    (async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        const user = JSON.parse(window.localStorage.getItem('user'));
        const provider = user?.provider || window.localStorage.getItem('tenantProvider');
        if (accessToken && isValidToken(accessToken) && provider) {
          saveActiveProvider(provider);
          setSession(accessToken);
          dispatch({
            type: 'INIT',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          if (accessToken && isValidToken(accessToken) && !provider) {
            setSession(null);
            clearAdminImpersonationSession();
            window.localStorage.removeItem('user');
            window.localStorage.removeItem('allBookingdata');
            clearActiveProvider();
          }
          dispatch({
            type: 'INIT',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INIT',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    })();
  }, []);

  if (!state.isInitialised) {
    return <MatxLoading />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        logout,
        register,
        switchCompany,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
